import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { suppliersAPI, purchasesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import SlideOver from '../../components/ui/SlideOver';
import { ArrowLeft, Edit, Eye, Plus } from 'lucide-react';
import SupplierForm from './SupplierForm';

const SupplierDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [supplier, setSupplier] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(true);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditSlideOver, setShowEditSlideOver] = useState(false);

  useEffect(() => {
    const fetchSupplier = async () => {
      if (!id) return;
      try {
        setSupplierLoading(true);
        setError('');
        const data = await suppliersAPI.getOne(id);
        setSupplier(data);
      } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message || 'Failed to load supplier';
        setError(status === 404 ? 'Supplier not found' : msg);
        setSupplier(null);
        console.error('Error fetching supplier:', err);
      } finally {
        setSupplierLoading(false);
      }
    };
    fetchSupplier();
  }, [id]);

  useEffect(() => {
    const fetchPurchases = async () => {
      if (!id) return;
      try {
        setPurchasesLoading(true);
        const response = await purchasesAPI.getAll({
          supplier_id: id,
          limit: 20,
        });
        setPurchases(response.purchases || []);
      } catch (err) {
        console.error('Error fetching purchases:', err);
        setPurchases([]);
      } finally {
        setPurchasesLoading(false);
      }
    };
    fetchPurchases();
  }, [id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      pending: 'warning',
      received: 'neutral',
      'part-paid': 'warning',
      'paid-in-full': 'success',
      cancelled: 'danger',
    };
    return variants[status] || 'neutral';
  };

  const handleEditSuccess = () => {
    setShowEditSlideOver(false);
    suppliersAPI.getOne(id).then(setSupplier).catch(console.error);
  };

  if (supplierLoading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--ds-accent-primary)]"></div>
            <p className="mt-4 text-body text-[var(--ds-text-muted)]">Loading supplier...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !supplier) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
            {error}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/tenant/suppliers')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Suppliers
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/tenant/suppliers')}
                    className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Suppliers
                  </Button>
                </div>
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  {supplier?.supplier_name || 'Supplier'}
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  {supplier?.supplier_code && `Code: ${supplier.supplier_code}`}
                  {supplier?.supplier_code && supplier?.email && ' • '}
                  {supplier?.email}
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditSlideOver(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/tenant/purchases/new?supplier_id=${id}`)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Purchase
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Supplier Card */}
          {supplier && (
            <Card className="mb-6 bg-white dark:bg-gray-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-300 font-bold text-xl flex-shrink-0">
                    {supplier.supplier_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100">
                        {supplier.supplier_name}
                      </h2>
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          supplier.is_active
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {supplier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {supplier.supplier_code && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Code: {supplier.supplier_code}
                      </p>
                    )}

                    {/* Contact & Address in grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      {(supplier.contact_name || supplier.email || supplier.phone || supplier.mobile) && (
                        <div>
                          <h4 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Contact
                          </h4>
                          <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                            {supplier.contact_name && <p>{supplier.contact_name}</p>}
                            {supplier.email && <p>{supplier.email}</p>}
                            {supplier.phone && <p>{supplier.phone}</p>}
                            {supplier.mobile && <p>{supplier.mobile}</p>}
                          </div>
                        </div>
                      )}
                      {(supplier.address_line1 || supplier.city || supplier.postcode) && (
                        <div>
                          <h4 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Address
                          </h4>
                          <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                            {supplier.address_line1 && <p>{supplier.address_line1}</p>}
                            {supplier.address_line2 && <p>{supplier.address_line2}</p>}
                            {(supplier.city || supplier.county) && (
                              <p>
                                {supplier.city}
                                {supplier.city && supplier.county && ', '}
                                {supplier.county}
                              </p>
                            )}
                            {supplier.postcode && <p>{supplier.postcode}</p>}
                            {supplier.country && <p>{supplier.country}</p>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Financial Terms */}
                    {(supplier.payment_terms ||
                      supplier.credit_limit != null ||
                      (supplier.current_balance !== undefined && supplier.current_balance !== null) ||
                      supplier.account_number) && (
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 mb-3">
                          Financial Terms
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          {supplier.payment_terms && (
                            <div>
                              <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Payment Terms
                              </FormLabel>
                              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                {supplier.payment_terms}
                              </p>
                            </div>
                          )}
                          {supplier.credit_limit != null && supplier.credit_limit !== '' && (
                            <div>
                              <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Credit Limit
                              </FormLabel>
                              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                {formatCurrency(supplier.credit_limit)}
                              </p>
                            </div>
                          )}
                          <div>
                            <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Current Balance
                            </FormLabel>
                            <p
                              className={`mt-1 text-sm font-medium ${
                                parseFloat(supplier.current_balance || 0) >= 0
                                  ? 'text-gray-900 dark:text-gray-100'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {formatCurrency(supplier.current_balance)}
                            </p>
                          </div>
                          {supplier.account_number && (
                            <div>
                              <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Account Number
                              </FormLabel>
                              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                {supplier.account_number}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {supplier.notes && (
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          Notes
                        </h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {supplier.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Purchases */}
          <Card className="overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
              <h3 className="text-h2 font-semibold text-gray-900 dark:text-gray-100">
                Recent Purchases
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/purchases/new?supplier_id=${id}`)}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Purchase
              </Button>
            </div>
            {purchasesLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-body text-[var(--ds-text-muted)]">
                  Loading purchases...
                </p>
              </div>
            ) : purchases.length === 0 ? (
              <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
                <p>No purchases from this supplier yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/tenant/purchases/new?supplier_id=${id}`)}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Purchase
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Purchase #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        <div className="font-medium text-[var(--ds-text-primary)]">
                          {purchase.purchaseNum}
                        </div>
                        {purchase.receiptNumber && (
                          <div className="text-xs text-[var(--ds-text-muted)]">
                            Receipt: {purchase.receiptNumber}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[var(--ds-text-primary)]">
                        {formatDate(purchase.purchaseDate)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-[var(--ds-text-primary)]">
                        {formatCurrency(purchase.total)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getStatusBadgeVariant(purchase.status)}
                          className="text-xs"
                        >
                          {purchase.status
                            ?.replace('-', ' ')
                            ?.replace(/\b\w/g, (l) => l.toUpperCase()) || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Tooltip content="View">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/purchases/${purchase.id}`)}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/purchases/${purchase.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        {/* Edit Slide-Over */}
        {supplier && (
          <SlideOver
            open={showEditSlideOver}
            onOpenChange={(open) => {
              setShowEditSlideOver(open);
            }}
            title="Edit Supplier"
            width="w-full max-w-2xl"
          >
            <SupplierForm
              supplier={supplier}
              onClose={() => setShowEditSlideOver(false)}
              onSuccess={handleEditSuccess}
            />
          </SlideOver>
        )}
      </Layout>
    </TooltipProvider>
  );
};

export default SupplierDetail;
